import api from '../utils/api';

/**
 * Upload a profile picture to the backend (R2)
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} The URL of the uploaded image from R2
 */
export const uploadImageFile = async (file) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }

  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('Image size must be less than 2MB');
  }

  const formData = new FormData();
  formData.append('profilePicture', file);

  try {
    const response = await api.post('/api/upload/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.url;
  } catch (error) {
    console.error('Upload error:', error);
    throw new Error(error.response?.data?.message || 'Failed to upload image');
  }
};

/**
 * Upload a general image to the backend (R2)
 * @param {File} file - The image file
 * @param {string} folder - Optional folder name in R2
 * @returns {Promise<string>} R2 URL
 */
export const uploadImage = async (file, folder = 'general') => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }

  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('Image size must be less than 5MB');
  }

  const formData = new FormData();
  formData.append('image', file);
  formData.append('folder', folder);

  try {
    const response = await api.post('/api/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.url;
  } catch (error) {
    console.error('Upload error:', error);
    throw new Error(error.response?.data?.message || 'Failed to upload image');
  }
};

/**
 * Delete an image from R2
 * @param {string} key - The R2 object key
 * @returns {Promise<void>}
 */
export const deleteImage = async (key) => {
  try {
    await api.delete('/api/upload/image', {
      data: { key },
    });
  } catch (error) {
    console.error('Delete error:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete image');
  }
};

/**
 * Upload an attachment (PDF, Docs, etc.) using presigned URL for direct upload to R2
 * This provides accurate progress tracking since the browser uploads directly to R2
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Optional progress callback (receives 0-100 percentage)
 * @param {string} folder - Optional folder name (defaults to 'attachments')
 * @returns {Promise<string>} The URL of the uploaded file
 */
export const uploadAttachment = async (file, onProgress, folder = 'attachments') => {
  const maxSize = 100 * 1024 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('File size must be less than 100GB');
  }

  try {
    const presignResponse = await api.post('/api/upload/presigned-url', {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      folder,
    });

    const { presignedUrl, fileUrl } = presignResponse.data;

    await uploadDirectToS3(presignedUrl, file, onProgress);

    return fileUrl;
  } catch (error) {
    console.error('Upload error:', error);
    if (error.message) {
      throw error;
    }
    throw new Error(error.response?.data?.message || 'Failed to upload attachment');
  }
};

/**
 * Upload directly to S3-compatible storage using presigned URL
 * @param {string} presignedUrl - The presigned URL from the server
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<void>}
 */
const uploadDirectToS3 = (presignedUrl, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentCompleted = Math.round((event.loaded * 100) / event.total);
        onProgress(percentCompleted);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', presignedUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
};

export default {
  uploadImageFile,
  uploadImage,
  uploadAttachment,
  deleteImage,
};

